import { Fragment, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import CarouselDates from '../components/Carousel';
import ExclusiveCreditCardBrand from '../components/ExclusiveCreditCardBrand';
import EmptyState from '../components/NoTickets';
import * as NoTicketsStyles from '../components/NoTickets/noTickets.styles';
import SummaryMobile from '../components/SummaryMobile';
import BackIcon from '../components/SvgCustom/svg/BackIcon';
import WaitlistModal from '../components/WaitlistModal';
import ZoomImageViewer from '../components/ZoomImageViwer';
import { CardsInfo } from '../constants/cards';
import { DictonaryKeys } from '../enums/noTickets';
import { useBreadCrumbsContext } from '../hooks/contexts/breadCrumb.context';
import { useCartContext } from '../hooks/contexts/cart.context';
import { DataCarouselProps, useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { ListView, useUiContext } from '../hooks/contexts/ui.context';
import { useUserContext } from '../hooks/contexts/user.context';
import { useResaleAvailabilityCheck } from '../hooks/useResaleAvailabilityCheck';
import { hasJoinedWaitlist } from '../services/waitlistService';
import * as Styles from '../styles/pages/tickets';

const TicketsPage = () => {
  const {
    allTicketsUnavailable,
    loading,
    startImage,
    isFootball,
    isBotafogo,
    hasTermModal,
    singleDayDate,
    selectedIdDateCarousel,
    dataCarousel,
    noTicketContentKey,
  } = useEventContext();
  const { translate } = useI18nContext();
  const { isPaymentPage } = useBreadCrumbsContext();
  const { isMobile, handleRenderListView, listView, handleChangeListView } = useUiContext();
  const { summaryData, combosInCart } = useCartContext();
  const {
    event,
    applyPassKey,
    applyCoupon,
    openModalBotafogo,
    showNoTicketsContent,
    openTermModal
  } = useEventContext();
  const theme = useTheme();
  const [queryParam] = useSearchParams();
  const passkey = queryParam.get('passkey');
  const coupon = queryParam.get('coupon');
  const { modalIsOpen, setContentModal, toggleModal, defineTitleModal } = useModalContext();
  const { isTicketPage } = useBreadCrumbsContext();
  const [selectedSession, setSelectedSession] = useState<DataCarouselProps>();

  const navigate = useNavigate();
  const noTicketsToDisplay = showNoTicketsContent
    && noTicketContentKey !== DictonaryKeys.eventNotFound
    && noTicketContentKey !== DictonaryKeys.ticketListError;
  const { hasResale, loading : resaleAvailabilityLoading } = useResaleAvailabilityCheck(
    noTicketsToDisplay ? event?.id : undefined
  );

  const alreadyJoined = event?.id ? hasJoinedWaitlist(event.id) : true;

  const { renewToken, handleAuthenticatedUser, authenticatedUser, user } = useUserContext();

  const openWaitlistModal = useCallback(() => {
    defineTitleModal(translate(['RESALE_LISTING', 'WAITLIST', 'TITLE']));
    setContentModal(
      <WaitlistModal
        eventId={event!.id}
        userId={user?.id}
      />
    );
    toggleModal({ open : true });
  }, [defineTitleModal, translate, setContentModal, event?.id, user?.id, toggleModal]);

  const handleBuyResaleClick = useCallback(
    () => navigate(`/resale/event/${event?.id}`),
    [navigate, event?.id]
  );

  const { cta : resaleCta, titleKey : resaleTitleKey, subtitleKey : resaleSubtitleKey } = useMemo(
    (): { cta: ReactNode; titleKey?: [string, string]; subtitleKey?: [string, string] } => {
      if (!noTicketsToDisplay) return { cta : null };
      if (resaleAvailabilityLoading) return { cta : null };
      const isSoldOut = noTicketContentKey === DictonaryKeys.soldOut;
      if (hasResale) {
        return {
          cta : (
            <NoTicketsStyles.CtaButton id="resale-cta-button" onClick={handleBuyResaleClick}>
              {translate(['RESALE_LISTING', 'BUY_RESALE_TICKETS'])}
            </NoTicketsStyles.CtaButton>
          ),
          ...(isSoldOut && {
            titleKey : ['RESALE_LISTING', 'SOLDOUT_TITLE'] as [string, string],
            subtitleKey : ['RESALE_LISTING', 'SOLDOUT_SUBTITLE'] as [string, string],
          }),
        };
      }
      const waitlistActive = Boolean(event?.attributes.secWaitlist?.value);
      if (waitlistActive && !alreadyJoined) {
        return {
          cta : (
            <NoTicketsStyles.CtaButton id="waitlist-cta-button" onClick={openWaitlistModal}>
              {translate(['RESALE_LISTING', 'ENTER_WAITLIST_EMPTY_STATE'])}
            </NoTicketsStyles.CtaButton>
          ),
          titleKey : ['RESALE_LISTING', 'WAITLIST_TITLE'] as [string, string],
          subtitleKey : ['RESALE_LISTING', 'WAITLIST_SUBTITLE'] as [string, string],
        };
      }
      return { cta : null };
    },
    [
      noTicketsToDisplay, resaleAvailabilityLoading, hasResale, alreadyJoined,
      noTicketContentKey, event?.attributes.secWaitlist?.value,
      translate, handleBuyResaleClick, openWaitlistModal,
    ]
  );

  useEffect(() => {
    renewToken();
  }, []);

  const showSummaryInMobile = isMobile && (summaryData.length > 0 || combosInCart.length > 0);
  const fullWidthContainer = isPaymentPage || allTicketsUnavailable || isMobile;
  const hasAUniqueSession = isFootball || singleDayDate;

  useEffect(() => {
    setSelectedSession(dataCarousel.find(({ id }) => id === selectedIdDateCarousel));
  }, [selectedIdDateCarousel]);

  useEffect(() => {
    if (passkey && event?.id && event?.producerId) {
      applyPassKey({ passkey, modalIsOpen, setContentModal, toggleModal });
    }
  }, [passkey, event?.id, event?.producerId]);

  useEffect(() => {
    if (coupon && event?.id && event?.producerId) {
      applyCoupon({ coupon, modalIsOpen, setContentModal, toggleModal });
    }
  }, [coupon, event?.id, event?.producerId]);

  useEffect(() => {
    if (isTicketPage && authenticatedUser) {
      handleAuthenticatedUser(false);
    }
  }, [isTicketPage]);

  useEffect(() => {
    if (isBotafogo) {
      openModalBotafogo({ setContentModal, toggleModal });
    }
  }, [isBotafogo]);

  useEffect(() => {
    if(hasTermModal) {
      openTermModal({ setContentModal, toggleModal });
    }
  }, [hasTermModal]);

  const exclusiveCreditCardBrand = event?.attributes.exclusiveCreditCardBrand?.value;

  return (
    <Styles.Container
      id="tickets-page"
      $fullWidth={fullWidthContainer}
      $summaryMobileIsOpen={showSummaryInMobile}
    >
      {exclusiveCreditCardBrand && <ExclusiveCreditCardBrand {...CardsInfo[exclusiveCreditCardBrand]} />}
      {
        (!isPaymentPage && !showNoTicketsContent ) ? (
          <Fragment>
            {isMobile ? (
              <Fragment>
                <CarouselDates />
                <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                  {startImage && (
                    <Styles.WrapperStartImage id="start-image-wrapper">
                      <Styles.ContainerImage id="start-image-container">
                        <ZoomImageViewer pathImage={startImage.large} />
                      </Styles.ContainerImage>
                    </Styles.WrapperStartImage>
                  )}
                  <div>
                    {listView === ListView.TICKETS && !hasAUniqueSession && (
                      <Styles.BackToSessionListContainer>
                        <Styles.RedirectText
                          onClick={() => handleChangeListView(ListView.SESSIONS)}
                        >
                          <img src={BackIcon({ colorTheme : theme.colors.accent.primary })} alt="seta voltando" />
                          {translate(['TICKET_TEMPLATE', 'VIEW_SESSIONS'])}
                        </Styles.RedirectText>
                        <Styles.RightContent>
                          <h3>{selectedSession?.title}</h3>
                          <span>{selectedSession?.subtitle}</span>
                        </Styles.RightContent>
                      </Styles.BackToSessionListContainer>
                    )}

                    {handleRenderListView()}
                  </div>
                </Styles.WrapperContent>
              </Fragment>
            ) : (
              <Fragment>
                <CarouselDates />
                <Styles.WrapperContent $showStartImage={Boolean(startImage)}>
                  {startImage && (
                    <Styles.WrapperStartImage id="start-image-wrapper">
                      <Styles.ContainerImage id="start-image-container">
                        <ZoomImageViewer pathImage={startImage.large} />
                      </Styles.ContainerImage>
                    </Styles.WrapperStartImage>
                  )}
                  {handleRenderListView()}
                </Styles.WrapperContent>
              </Fragment>
            )}
          </Fragment>
        ) : !loading && <EmptyState cta={resaleCta} titleKey={resaleTitleKey} subtitleKey={resaleSubtitleKey} />
      }
      {showSummaryInMobile && <SummaryMobile />}
    </Styles.Container>
  );
};

export default TicketsPage;
